import { t } from "i18next";
import { match, P } from "ts-pattern";
import { z } from "zod";

import {
  longTextSchema,
  nonFutureDateTimeSchema,
  shortTextSchema,
  yesOnlySchema,
} from "@thalamos/common";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import { tk } from "../../types/Translation.js";
import {
  getFirstPlaceOfSafetyArrivalDateTime,
  isPoliceLeftTimeOutOfExpectedRange,
} from "../../workflowLogic/calculateDetentionTime.js";
import { getSupportedIncidentTypeSelection } from "../../workflowLogic/helpers/getIncidentTypeSelection.js";
import { getMcaAccompaniment } from "../../workflowLogic/helpers/getMcaAccompaniment.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";
import { hasSharedForm } from "../../workflowLogic/helpers/hasSharedForm.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldConditionallyVisible({
    fieldName: "confirmEndForm",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).build(),
    label: tk("finishForm.confirmEndForm.title"),
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => true)
        .with("136", () => false)
        .with(undefined, () => false)
        .exhaustive() &&
      match(getMcaAccompaniment({ incidentWorkItem }).unwrapOr("no"))
        .with("no", () => true)
        .with("yes", () => false)
        .exhaustive(),
  })

  .withFieldConditionallyVisible({
    fieldName: "whyNotSharing",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "nhsUsingOtherProcess" as const,
          "internetConnectivityIssue" as const,
          "emailNotReceived" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("finishForm.whyNotSharing.title"),
    requiredLabel: tk("finishForm.whyNotSharing.required"),
    options: [
      {
        label: tk("finishForm.whyNotSharing.options.nhsUsingOtherProcess"),
        value: "nhsUsingOtherProcess",
      },
      {
        label: tk("finishForm.whyNotSharing.options.internetConnection"),
        value: "internetConnectivityIssue",
      },
      {
        label: tk("finishForm.whyNotSharing.options.emailNotReceived"),
        value: "emailNotReceived",
      },
      {
        label: tk("finishForm.whyNotSharing.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => true)
        .with("136", () => true)
        .with(undefined, () => true)
        .exhaustive() &&
      match(getMcaAccompaniment({ incidentWorkItem }).unwrapOr("no"))
        .with("no", () => false)
        .with("yes", () => true)
        .exhaustive() &&
      !hasSharedForm({ incidentWorkItem }),
  })
  .withFieldConditionallyVisible({
    fieldName: "whyNotSharingOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) => formValues.whyNotSharing?.value === "other",
    label: tk("finishForm.whyNotSharingOther.title"),
    multiline: true,
    rows: "5",
    requiredLabel: tk("finishForm.whyNotSharingOther.required"),
  })
  .withFieldConditionallyVisible({
    fieldName: "staffNameLeftWith",
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match([
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
        getMcaAccompaniment({ incidentWorkItem }).unwrapOr("no"),
      ])
        .with(["mca", "no"], () => false)
        .with(["mca", "yes"], () => true)
        .with(["136", P._], () => false)
        .with([undefined, P._], () => true)
        .exhaustive(),
    label: tk("finishForm.staffNameLeftWith.title"),
    requiredLabel: tk("finishForm.staffNameLeftWith.required"),
  })

  .withFieldConditionallyVisible({
    fieldName: "dateTimeLeft",
    type: "date-time",
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("endIncidentCommon.incidentLeaveDatePos"),
    timeLabel: tk("endIncidentCommon.incidentLeaveTimePos"),
    disableFuture: true,
    isVisible: ({ formValues }) => formValues.confirmEndForm?.value !== "yes",
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "incidentDuration",
    type: "incident-duration",
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("finishForm.totalPosTime"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    getDateTimeLeft: ({ formValues }) => formValues.dateTimeLeft?.value,
    getArrivalAtFirstPlaceOfSafetyDateTime: ({ incidentWorkItem }) =>
      getFirstPlaceOfSafetyArrivalDateTime(incidentWorkItem),
    isVisible: ({ formValues }) => formValues.confirmEndForm?.value !== "yes",
  })
  .withFieldConditionallyVisible({
    fieldName: "confirmDateTimeLeaving",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    label: tk("finishForm.confirmDateTimeLeaving"),
    requiredLabel: tk("finishForm.confirmDateTimeLeavingRequired"),
    isVisible: ({ formValues, incidentWorkItem }) => {
      const dateTimeLeft = formValues.dateTimeLeft ?? undefined;
      if (dateTimeLeft === undefined) {
        return false;
      }

      return isPoliceLeftTimeOutOfExpectedRange(
        dateTimeLeft.value,
        incidentWorkItem,
      );
    },
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "dutyOfCareBanner",
    type: "banner",
    bannerType: "warning",
    body: ({ incidentWorkItem }) => ({
      translationKey: tk("finishForm.informStaff"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      (match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("136", () => true)
        .with("mca", () => false)
        .with(undefined, () => false)
        .exhaustive() ||
        match(getMcaAccompaniment({ incidentWorkItem }).unwrapOr("no"))
          .with("no", () => false)
          .with("yes", () => true)
          .exhaustive()),
  })
  .withFieldConditionallyVisible({
    fieldName: "selectedAssessmentOutcome",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "mentalExamination",
          "signpostedToCommunity",
          "informalAdmission",
          "section2Detention",
          "section3Detention",
          "unknownHandover",
          "unknownMaximumDetention",
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("endIncidentCommon.incidentOutcome.title"),
    options: [
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.mentalExamination",
        ),
        value: "mentalExamination",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.signpostedToCommunity",
        ),
        value: "signpostedToCommunity",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.informalAdmission",
        ),
        value: "informalAdmission",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.section2Detention",
        ),
        value: "section2Detention",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.section3Detention",
        ),
        value: "section3Detention",
      },
      {
        label: tk("endIncidentCommon.incidentOutcome.options.unknownHandover"),
        value: "unknownHandover",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.unknownMaximumDetention",
        ),
        value: "unknownMaximumDetention",
      },
    ],
    isVisible: ({ formValues, incidentWorkItem }) =>
      formValues.confirmEndForm?.value !== "yes" &&
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("136", () => true)
        .with("mca", () => false)
        .with(undefined, () => false)
        .exhaustive(),
  })
  .withPage(["confirmEndForm"]) // MCA - without accompaniment]
  .withPage([
    "whyNotSharing", // MCA - with accompaniment, without sharing
    "whyNotSharingOther", // MCA - with accompaniment, without sharing
  ])
  .withPage([
    "staffNameLeftWith", // MCA - with accompaniment, with sharing
  ])
  .withPage([
    "dateTimeLeft",
    "incidentDuration",
    "confirmDateTimeLeaving",
    "dutyOfCareBanner",
  ])
  .withPage(["selectedAssessmentOutcome"]) // 136 only
  .build({ t });

export type FinishFormData = ReturnType<typeof formSpecification>["formType"];
